/**
 * Created by michaelbray on 6/13/18.
 */

import React from 'react';
import PropTypes from 'prop-types';
import ReactCrop, { makeAspectCrop } from 'react-image-crop';
import cx from 'classnames';
import { getPixelCrop } from './utils/imageHelpers';
import Loading from '../Loading';

const propTypes = {
    height: PropTypes.number,
    uploadImageSource: PropTypes.string,
    saveCroppedPhoto: PropTypes.func,
    cancelCrop: PropTypes.func,
    disabled: PropTypes.bool,
    aspect: PropTypes.number,
    isFreeform: PropTypes.bool,
    hideDesiredDimensions: PropTypes.bool,
    desiredWidth: PropTypes.number,
    desiredHeight: PropTypes.number,
    previousCrop: PropTypes.object,
    imageHasBeenPublished: PropTypes.bool.isRequired
};

const defaultProps = {
    aspect: 4 / 3,
    desiredWidth: 640,
    desiredHeight: 480,
    isFreeform: false,
    hideDesiredDimensions: false,
    previousCrop: {}
};

export default class PhotoCropper extends React.Component {
    constructor(props) {
        super(props);

        // The crop, pixelCrop, minWidth, and minHeight numbers are percentages of the uploaded image
        // We are setting minWidth to 100% to ensure the user can only cut off the top and bottom
        // of the original image.
        this.state = {
            isLoading: true,
            initialDisable: false,
            crop: {
                x: 0,
                y: 0,
                width: props.isFreeform ? 50 : 40,
                height: props.isFreeform ? 50 : 30
            },
            pixelCrop: {
                x: 0,
                y: 0,
                width: 0,
                height: 0
            },
            minHeight: 0
        };

        this.onImageLoaded = this.onImageLoaded.bind(this);
        this.onCropChange = this.onCropChange.bind(this);
        this.saveCroppedPhoto = this.saveCroppedPhoto.bind(this);
    }

    onImageLoaded = (image) => {
        const { previousCrop, height, aspect, isFreeform } = this.props;
        let newCrop;

        if (previousCrop.height && !isFreeform) {
            newCrop = makeAspectCrop(
                {
                    x: previousCrop.x,
                    y: previousCrop.y,
                    aspect,
                    height: previousCrop.height
                },
                image.width / image.height
            );
        }
        else if (previousCrop && isFreeform) {
            newCrop = {
                x: previousCrop.x,
                y: previousCrop.y,
                width: previousCrop.width,
                height: previousCrop.height
            };
        }
        else if (isFreeform) {
            newCrop = {
                x: 0,
                y: 0,
                width: 50,
                height: 50
            };
        }
        else {
            newCrop = makeAspectCrop(
                {
                    x: 0,
                    y: 0,
                    aspect,
                    height: (image.height / height) * 100
                },
                image.width / image.height
            );
        }

        this.setState({
            isLoading: false,
            crop: newCrop,
            // In order for us to have a valid initial state, we must calculate the pixel dimensions of the image in
            // case the user never manipulates the crop square and we never get an `onCropChange` called. We will use
            // the same method that `react-image-crop` uses internally (copied into our helper) to ensure that we get
            // as close to the same result as possible. At this time there is no mechanism with `react-image-crop` to
            // poke it such that it will fire its `onChange` delegate method.
            pixelCrop: getPixelCrop(image, newCrop),
            minHeight: (340 / height) * 100
        });
    };

    onCropChange(crop, pixelCrop) {
        // The crop property reflects what the user sees on the screen
        // The pixelCrop property is relative to the original image and
        // will be passed to a helper function
        this.setState({
            crop,
            pixelCrop,
            initialDisable: false
        });
    }

    saveCroppedPhoto() {
        // The pixelCrop property includes information about the original image
        // that the imageCropperHelper.cropImage function uses to generate
        // a brand new cropped image
        this.props.saveCroppedPhoto(this.state.pixelCrop);
    }

    render() {
        const { desiredWidth, desiredHeight, hideDesiredDimensions, cancelCrop, disabled, uploadImageSource, imageHasBeenPublished } = this.props;
        const { pixelCrop, crop, minHeight, initialDisable, isLoading } = this.state;
        const isDisabled = disabled || initialDisable;
        const isImageTooSmall = pixelCrop.width < desiredWidth || pixelCrop.height < desiredHeight;

        return (
            <div className="rec-photo-cropper">
                <div className="rec-photo-cropper-reactcrop">
                    <ReactCrop
                        src={uploadImageSource}
                        crop={crop}
                        minHeight={minHeight}
                        onImageLoaded={this.onImageLoaded}
                        onChange={this.onCropChange}
                        onComplete={this.onComplete} />
                    {isLoading && (
                        <div className="rec-photo-cropper-loading">
                            <Loading />
                        </div>
                    )}
                </div>

                <div className="rec-photo-cropper-control-area">
                    <div>
                        <div className={cx({ 'rec-photo-manager-error-text': isImageTooSmall })}>
                            Current dimensions: <strong>{pixelCrop.width}</strong> x <strong>{pixelCrop.height}</strong>
                            px
                        </div>
                        {!hideDesiredDimensions && (
                            <div>
                                Minimum dimensions: <strong>{desiredWidth}</strong> x <strong>{desiredHeight}</strong>
                                px
                            </div>
                        )}
                    </div>
                    <div className="rec-photo-cropper-action-buttons">
                        <button className="rec-button-tertiary-alt-small" onClick={cancelCrop}>
                            Cancel
                        </button>
                        <button className="rec-button-primary-small" onClick={this.saveCroppedPhoto} disabled={isDisabled}>
                            {imageHasBeenPublished ? 'Publish New Crop' : 'Done'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

PhotoCropper.propTypes = propTypes;
PhotoCropper.defaultProps = defaultProps;
